import defaultConfig from '../../config/responseMappings/default.json';
import { parseModulePath } from '../../utils/modulePath';

// Any src/config/responseMappings/<moduleName>.json is auto-registered as a
// per-module override, shallow-merged over defaultConfig — no code change needed.
const overrideConfigs = import.meta.glob('../../config/responseMappings/*.json', { eager: true });

function getModuleOverride(moduleName) {
  if (!moduleName) return null;

  for (const path of Object.keys(overrideConfigs)) {
    const fileName = path.split('/').pop().replace(/\.json$/, '');
    if (fileName === moduleName) {
      const mod = overrideConfigs[path];
      return mod.default ?? mod;
    }
  }

  return null;
}

function loadMappingConfig(moduleName) {
  const override = getModuleOverride(moduleName);
  return override ? { ...defaultConfig, ...override } : defaultConfig;
}

function getPath(source, path) {
  return String(path).split('.').reduce((value, key) => (value == null ? undefined : value[key]), source);
}

function resolveFirst(response, payload, spec) {
  if (!spec) return undefined;
  const sourceMap = { response, payload };

  for (const sourceName of spec.sources) {
    for (const path of spec.paths) {
      const value = getPath(sourceMap[sourceName], path);
      if (value !== undefined) return value;
    }
  }

  return undefined;
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const number = Number(value);
    if (Number.isFinite(number)) return number;
  }

  return undefined;
}

function humanizeKey(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase());
}

// Substrings that flag a key as sensitive; synthesis must never surface these
// automatically even though they are primitive values. See
// docs/JSON-Driven-Response-Mapping-Security-Assessment.html — "Accidental
// sensitive-data exposure".
const SENSITIVE_KEY_PATTERN = /token|password|passwd|secret|apikey|api_key|auth|credential|salary|ssn|ein\b/i;

function isSensitiveKey(key) {
  return SENSITIVE_KEY_PATTERN.test(key);
}

export function synthesizeFieldsFromRows(rows) {
  const firstRow = rows[0];
  if (!firstRow) return [];

  return Object.keys(firstRow)
    .filter((key) => !key.startsWith('_') && !isSensitiveKey(key) && typeof firstRow[key] !== 'object')
    .map((key) => ({
      value: key,
      field: key,
      label: humanizeKey(key),
      isVisible: true,
    }));
}

export function applyVisibilityFlag(fields, flagKey) {
  if (!flagKey) return fields;

  return fields
    .filter((field) => field[flagKey] === 1 || field[flagKey] === true || field[flagKey] === undefined)
    .map((field) => ({ ...field, isVisible: true }));
}

// Collection caps: an untrusted response with huge arrays can freeze the
// browser (see "Unbounded response size" in the security assessment).
const MAX_ROWS = 5000;
const MAX_FIELDS = 300;
const MAX_TABS = 100;
const MAX_ACTIONS = 100;
const MAX_ACTION_RULES = 300;
const MAX_COLUMN_ACTIONS = 100;

function isPlainObject(value) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function clampArray(value, max) {
  return Array.isArray(value) ? value.slice(0, max) : [];
}

// Backend metadata is trusted with minimal validation ("Backend metadata is
// trusted" finding). A field/tab/action array must at least be an array of
// plain objects before it reaches ListView.
function sanitizeObjectList(value, max) {
  return clampArray(value, max).filter(isPlainObject);
}

function sanitizeFieldList(value, max) {
  return sanitizeObjectList(value, max).filter(
    (field) => typeof field.value === 'string' || typeof field.field === 'string'
  );
}

// javascript:/data:/vbscript:/file: URLs in action metadata can reach a
// rendered <a href> (ListView's renderActionButton) and execute in the
// user's session. Only allow relative routes and standard web protocols.
const UNSAFE_URL_PROTOCOL = /^\s*(javascript|data|vbscript|file):/i;
const ACTION_URL_KEYS = ['url', 'href', 'routeUrl', 'routeURL'];

function sanitizeAction(action) {
  const safe = { ...action };
  for (const key of ACTION_URL_KEYS) {
    if (typeof safe[key] === 'string' && UNSAFE_URL_PROTOCOL.test(safe[key])) {
      delete safe[key];
    }
  }
  return safe;
}

function sanitizeActionList(value, max) {
  return sanitizeObjectList(value, max).map(sanitizeAction);
}

// Dot-notation module names ("job.client", "job.client.company") display the
// data nested at that path inside each fetched record instead of the record
// itself. Objects become one row each, arrays are flattened into one row per
// entry, and primitives are wrapped as { value } so they still render.
function extractNestedRows(rows, nestedPath) {
  return rows.flatMap((row) => {
    const value = getPath(row, nestedPath);
    if (value === undefined || value === null) return [];

    const entries = Array.isArray(value) ? value : [value];
    return entries
      .filter((entry) => entry !== undefined && entry !== null)
      .map((entry) => (isPlainObject(entry) ? entry : { value: entry }));
  });
}

export function mapListResponse(response, moduleName) {
  const { baseModule, nestedPath } = parseModulePath(moduleName);
  const config = loadMappingConfig(baseModule);
  const payload = response?.data ?? response;

  const rowsRaw = Array.isArray(payload) ? payload : resolveFirst(response, payload, config.rows);
  let rows = clampArray(rowsRaw, MAX_ROWS);
  if (nestedPath) {
    rows = clampArray(extractNestedRows(rows, nestedPath), MAX_ROWS);
    console.log(`[mapListResponse] module "${baseModule}" → nested path "${nestedPath}": ${rows.length} row(s)`);
  }

  const totalRaw = resolveFirst(response, payload, config.total);
  // Some backends nest the total under an object (e.g. { total, searchCount,
  // count }) instead of a flat number; Number(object) is NaN, so unwrap it
  // first or the count silently collapses to 0.
  const unwrappedTotal = isPlainObject(totalRaw)
    ? firstFiniteNumber(totalRaw.total, totalRaw.searchCount, totalRaw.count)
    : totalRaw;
  const total = Number(unwrappedTotal ?? rows.length) || 0;

  const tabs = sanitizeObjectList(resolveFirst(response, payload, config.tabs), MAX_TABS);

  // In nested mode the response's field config describes the parent module's
  // records, not the nested objects being displayed — always synthesize the
  // columns from the nested rows themselves.
  const fieldsRaw = nestedPath ? [] : resolveFirst(response, payload, config.fields);
  let fields = Array.isArray(fieldsRaw) ? fieldsRaw : [];
  if (fields.length === 0 && (nestedPath || config.synthesizeFieldsFromRows)) {
    fields = synthesizeFieldsFromRows(rows);
  }
  fields = sanitizeFieldList(fields, MAX_FIELDS);
  if (config.visibilityFlag) {
    fields = applyVisibilityFlag(fields, config.visibilityFlag);
  }

  const tabFieldRaw = resolveFirst(response, payload, config.tabField);
  const tabField = typeof tabFieldRaw === 'string' ? tabFieldRaw : '';

  return {
    rows,
    total,
    tabs,
    fields,
    tabField,
    actionRules: sanitizeActionList(resolveFirst(response, payload, config.actionRules), MAX_ACTION_RULES),
    actions: sanitizeActionList(resolveFirst(response, payload, config.actions), MAX_ACTIONS),
    columnActions: sanitizeActionList(resolveFirst(response, payload, config.columnActions), MAX_COLUMN_ACTIONS),
  };
}
